/**
 * useSelectedProviders — single source of truth for which clouds
 * the current user has configured.
 *
 * Behaviour:
 *   - Demo user (demo@cartieai.com): shouldShowAll = true, sees all 15 clouds
 *   - Real user who completed onboarding: providers = ['aws', ...], shouldShowAll = false
 *   - Real user who hasn't selected any: hasSelection = false → show empty state
 *
 * Use this hook on every data-bound page so users see only what they actually use.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { CLOUD_PROVIDERS } from '../constants/cloudProviders';

const readProviders = () => {
  try {
    return JSON.parse(localStorage.getItem('selected_providers') || '[]');
  } catch {
    return [];
  }
};

export const useSelectedProviders = () => {
  const { user } = useAuth();
  const [providers, setProviders] = useState(readProviders);

  // Re-read on mount + listen for storage events (e.g. after onboarding)
  useEffect(() => {
    const refresh = () => setProviders(readProviders());
    refresh();
    window.addEventListener('storage', refresh);
    window.addEventListener('providers-updated', refresh);
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('providers-updated', refresh);
    };
  }, []);

  const isDemo = user?.email === 'demo@cartieai.com';
  const isAdmin = user?.role === 'admin';
  const hasSelection = providers.length > 0;

  // Demo sees everything. Admin sees everything when browsing.
  const shouldShowAll = isDemo || isAdmin;

  // The effective list to render UI against
  const effectiveProviders = shouldShowAll
    ? CLOUD_PROVIDERS.map((p) => p.id)
    : providers;

  // Detail objects (with logos, colors) for the user's actual clouds
  const providerDetails = effectiveProviders
    .map((id) => CLOUD_PROVIDERS.find((p) => p.id === id))
    .filter(Boolean);

  /** Returns true if the given provider id should be shown for this user. */
  const isActive = (providerId) => {
    if (!providerId) return true;
    if (shouldShowAll) return true;
    return providers.includes(providerId);
  };

  /** Filter an array of items that have a `provider` or `service` field. */
  const filterByProvider = (items = []) => {
    if (shouldShowAll) return items;
    if (!hasSelection) return [];
    return items.filter((item) => {
      const hay = (item.provider || item.service || item.cloud || '').toString().toLowerCase();
      return providers.some((p) => hay.includes(p));
    });
  };

  /** Returns the display name(s) of the user's primary cloud — for empty state copy. */
  const getPrimaryName = () => {
    if (shouldShowAll) return 'all clouds';
    if (!hasSelection) return 'your cloud';
    return providerDetails.map((p) => p.name).join(', ');
  };

  return {
    providers,              // raw ids: ['aws', 'oracle']
    providerDetails,        // [{id, name, LogoComponent, ...}]
    effectiveProviders,     // what to actually render
    isDemo,
    isAdmin,
    hasSelection,
    shouldShowAll,
    isActive,
    filterByProvider,
    getPrimaryName,
  };
};

export default useSelectedProviders;
